import { useRef } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useWhisperRecorder } from "@/lib/useWhisperRecorder";

/**
 * Textarea mit Diktier-Funktion auf Whisper-1 Basis.
 * - Nimmt Audio via MediaRecorder auf (funktioniert auf iOS Safari, Android Chrome, Desktop)
 * - Schickt das Audio an /api/ai/transcribe und fügt den transkribierten Text an
 * - Mehrfach-Diktate werden mit Leerzeichen verkettet
 */
export const DictaphoneTextarea = ({
  value,
  onChange,
  placeholder = "Notiz eingeben oder Mikrofon-Button drücken zum Diktieren…",
  rows = 3,
  testid = "dictaphone",
  className = "",
  lang = "de",
}) => {
  const baseRef = useRef("");

  const appendTranscript = (text) => {
    const current = baseRef.current || value || "";
    const merged = (current ? current + " " : "") + text.trim();
    const clean = merged.replace(/\s+/g, " ").trim();
    baseRef.current = clean;
    onChange(clean);
  };

  const recorder = useWhisperRecorder({
    language: lang,
    onTranscript: appendTranscript,
    onError: (msg) => toast.error(msg),
  });

  const onMicClick = () => {
    if (recorder.transcribing) return;
    if (recorder.recording) {
      recorder.stop();
    } else {
      baseRef.current = value || "";
      recorder.start();
    }
  };

  const showMicButton = recorder.supported;

  return (
    <div className="relative">
      <textarea
        data-testid={testid}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); baseRef.current = e.target.value; }}
        rows={rows}
        placeholder={placeholder}
        className={`w-full rounded-xl px-4 py-3 pr-14 text-[15px] resize-none focus:ring-1 transition outline-none border ${className}`}
      />
      {showMicButton && (
        <button
          type="button"
          data-testid={`${testid}-mic-btn`}
          onClick={onMicClick}
          disabled={recorder.transcribing}
          className={`absolute right-2 top-2 h-10 w-10 rounded-lg flex items-center justify-center transition active:scale-95 disabled:opacity-60 ${
            recorder.recording
              ? "bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.6)] animate-pulse"
              : "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 hover:bg-[#00E575]/25"
          }`}
          title={
            recorder.transcribing
              ? "Transkribiere…"
              : recorder.recording
              ? "Aufnahme beenden"
              : "Diktieren starten (Deutsch)"
          }
          aria-label={recorder.recording ? "Stop" : "Diktieren"}
        >
          {recorder.transcribing ? (
            <Loader2 size={18} className="animate-spin" />
          ) : recorder.recording ? (
            <MicOff size={18} />
          ) : (
            <Mic size={18} />
          )}
        </button>
      )}
      {recorder.recording && (
        <div className="absolute bottom-2 left-3 text-[12px] text-red-400 font-mono inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          REC · sprechen…
        </div>
      )}
      {recorder.transcribing && (
        <div className="absolute bottom-2 left-3 text-[12px] text-[#00E575] font-mono inline-flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Transkribiere…
        </div>
      )}
    </div>
  );
};
